'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Separator,
} from '@baseball-near-you/ui';
import { ReadMore } from '@bb/components/ReadMore';
import { TrackingButton } from '@bb/components/TrackingButton/TrackingButton';
import { trackClick } from '@bb/components/TrackingButton/trackClick';
import { ClickEventType } from '@bb/components/TrackingButton/types';
import type { PostCategory, Product, ProductSpecs } from '@bb/sanity.types';
import { Check, Star, XIcon } from 'lucide-react';
import Link from 'next/link';
import { ContentRenderer } from '../ContentRenderer';
import { SanityImageComponent } from '../SanityImageComponent';

export function ProductCard(props: {
  product: Product;
  category?: Pick<PostCategory, 'name' | 'slug'>;
}) {
  return props.product.specs ? (
    <div className="product-card group flex w-full flex-col rounded-md border p-3 shadow-xs md:min-w-full">
      <div className="contents md:flex md:w-full md:min-w-full md:flex-col">
        <div className="flex flex-col sm:mb-2 sm:flex-row sm:space-x-4">
          <div className="mb-2 h-min overflow-hidden rounded-sm sm:mb-0 sm:w-2/5 lg:h-full">
            <SanityImageComponent
              value={props.product.image}
              style={{
                margin: 0,
              }}
            />
          </div>
          <div className="flex flex-col justify-between sm:w-3/5">
            <div className="flex flex-col pt-1">
              <h4 className="-mb-[0.01rem]! font-normal! text-red text-xs">
                {(props.product.brand as any)?.name}
              </h4>
              <h2 className="title -mt-0!" style={{ lineHeight: '28px' }}>
                {props.product.name}
              </h2>

              <div className="flex items-center justify-start text-lg">
                <p className="mr-2 w-min font-light! text-black! text-lg!">
                  ${props.product.price}
                </p>
                <div className="flex items-center">
                  <Star size={16} className="w-4 fill-red text-red" />
                  <p className="mt-[0.1rem]! mb-0! ml-0.5 w-min font-normal! text-[0.8rem]! text-red!">
                    {props.product.rating}
                  </p>
                </div>
              </div>
            </div>
            {!!props.product.specs && (
              <div className="flex flex-col">
                <h4 className="my-0! font-normal! text-base!">Specs</h4>
                <div className="flex flex-wrap gap-1.5">
                  {(props.product.specs as unknown as ProductSpecs[]).map(
                    (spec) => (
                      <div
                        key={spec._id}
                        className="inline-block w-min whitespace-nowrap rounded-lg border border-gray-700 px-2 py-1 font-normal text-xs"
                      >
                        {spec.name}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="contents md:flex md:flex-col">
          <div className="contents md:flex md:flex-col">
            <Separator className="my-2" />
            <div className="flex flex-col">
              <a
                target="_blank"
                rel="nofollow noreferrer"
                href={props.product.link}
                className="mb-1"
              >
                <TrackingButton
                  analyticsEvent={{
                    name: ClickEventType.ProductCardClicked,
                    eventProperties: {
                      name: props.product.name,
                      price: props.product.price,
                      location: 'button',
                      valueOfLink: 'Shop Now',
                    },
                  }}
                  className="w-full bg-red text-lg transition-all hover:scale-[101%] hover:bg-red/90"
                >
                  Shop Now
                </TrackingButton>
              </a>
              <div className="mt-1 flex justify-between text-xs">
                <a
                  target="_blank"
                  rel="nofollow noreferrer"
                  href={props.product.link}
                  className="font-normal! text-gray-500!"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      await trackClick({
                        name: ClickEventType.ProductCardClicked,
                        eventProperties: {
                          name: props.product.name,
                          price: props.product.price,
                          location: 'sizes',
                          valueOfLink: props.product.sizes,
                        },
                      });
                    }}
                  >
                    Sizes: {props.product.sizes}
                  </button>
                </a>
                <a
                  target="_blank"
                  rel="nofollow noreferrer"
                  href={props.product.link}
                  className="font-normal! text-gray-500!"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      await trackClick({
                        name: ClickEventType.ProductCardClicked,
                        eventProperties: {
                          name: props.product.name,
                          price: props.product.price,
                          location: 'colors',
                          valueOfLink: props.product.colors,
                        },
                      });
                    }}
                  >
                    Colors: {props.product.colors}
                  </button>
                </a>
                <Link
                  href={`/blog/category/${(props.product.categories?.[0] as any)?.slug.current}`}
                  className="font-normal! text-gray-500!"
                >
                  Category:{' '}
                  {
                    (props.product.categories as unknown as PostCategory[])?.[0]
                      ?.name
                  }
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Accordion type="multiple">
        {(props.product.pros || props.product.cons) && (
          <AccordionItem
            value="our-take"
            className="mt-3! border-t border-b-0 pt-2"
          >
            <AccordionTrigger
              className="title py-1!"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Our take
            </AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 gap-y-3 pb-0 lg:grid-cols-2 lg:gap-x-8">
              <div className="flex flex-col border-b pb-3 md:w-full md:border-gray-100 lg:border-r lg:border-b-0 lg:pb-0">
                <h3 className="title text-base!">Pros</h3>
                <ul className="mt-1" style={{ paddingInlineStart: 0 }}>
                  {props.product.pros?.map((pro) => (
                    <li key={pro} className="list-inside list-none">
                      <div className="flex">
                        <Check
                          className="mt-[0.1rem] mr-1 text-green-500"
                          size={18}
                        />
                        <span className="font-normal text-gray-800 text-sm">
                          {pro}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col md:w-full">
                <h3 className="title text-base!">Cons</h3>
                <ul className="mt-1" style={{ paddingInlineStart: 0 }}>
                  {props.product.cons?.map((con) => (
                    <li key={con} className="list-inside list-none">
                      <div className="flex">
                        <XIcon
                          className="mt-[0.1rem] mr-1 text-red"
                          size={18}
                        />
                        <span className="font-normal text-gray-800 text-sm">
                          {con}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
        {props.product.description && (
          <AccordionItem
            value="description"
            className="mt-2! border-t border-b-0 pt-2"
          >
            <AccordionTrigger className="title py-1!">
              Description
            </AccordionTrigger>
            <AccordionContent className="pb-0">
              <ContentRenderer content={props.product.description} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  ) : (
    <a
      href={props.product.link}
      target="_blank"
      rel="nofollow noreferrer"
      className="group flex w-full xl:w-2/3"
      onClick={async () => {
        await trackClick({
          name: ClickEventType.ProductCardClicked,
          eventProperties: {
            name: props.product.name,
            price: props.product.price,
            location: 'old product card - container',
          },
        });
      }}
    >
      <div className="group-hover:-translate-y-0.5 my-3 w-full rounded-md border px-4 pt-4 pb-5 shadow-xs transition-all group-hover:scale-[100.5%] group-hover:shadow-md">
        <h2 className="title mt-0! text-xl! transition-colors group-hover:underline">
          {props.product.name}
          {' - '}
          <span>${props.product.price}</span>
        </h2>
        <SanityImageComponent
          value={props.product.image}
          style={{
            maxHeight: '14rem',
          }}
        />
        {props.product.description && (
          <ReadMore text={props.product.description as any} charCount={300} />
        )}
        <TrackingButton
          analyticsEvent={{
            name: ClickEventType.ProductCardClicked,
            eventProperties: {
              name: props.product.name,
              price: props.product.price,
              location: 'old product card - button',
            },
          }}
          className="mt-3 w-full bg-red transition-transform group-hover:scale-[101%]"
        >
          Buy Now
        </TrackingButton>
      </div>
    </a>
  );
}
